import { Play } from "lucide-react"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import type { MatchRecap, RecapKeyPlay } from "@/types"

import type { BeatState } from "./beat-calculator"
import { CommentaryDesk } from "./commentary-desk"
import { KeyPlayExpandedOverlay } from "./key-play-expanded-overlay"
import { KeyPlayHeader } from "./key-play-header"

/** Fast start, smooth settle — t³ ease-out */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

interface KeyPlayBeatProps {
  play: RecapKeyPlay
  playIndex: number
  beatState: BeatState
  recap: MatchRecap
  homeTeamId: string
  homeTeamHue: number
  awayTeamHue: number
}

export function KeyPlayBeat({
  play,
  playIndex,
  beatState,
  recap,
  homeTeamId,
  homeTeamHue,
  awayTeamHue,
}: KeyPlayBeatProps) {
  const [expanded, setExpanded] = useState<
    "image" | "replay" | "commentary" | null
  >(null)

  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : 800,
  )

  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  const hasReplay = !!(
    play.replay_snapshot &&
    play.replay_events &&
    recap.players_data
  )

  const handleExpand = (which: "image" | "replay" | "commentary") => {
    setExpanded(which)
  }

  const handleCollapse = () => {
    setExpanded(null)
  }

  if (!beatState.active) return null

  const commentatorMap = Object.fromEntries(
    recap.commentators.map((c) => [c.key, c]),
  )

  return (
    <div
      className="absolute inset-0"
      style={{
        opacity: beatState.containerOpacity,
        transform: `scale(${beatState.containerScale}) rotate(${beatState.containerRotate}deg)`,
        filter: `blur(${beatState.containerBlur}px)`,
        willChange: "opacity, transform, filter",
      }}
    >
      {/* Expanded overlay */}
      {expanded && (
        <KeyPlayExpandedOverlay
          mode={expanded}
          onCollapse={handleCollapse}
          play={play}
          recap={recap}
          hasReplay={hasReplay}
          homeTeamId={homeTeamId}
          homeTeamHue={homeTeamHue}
          awayTeamHue={awayTeamHue}
        />
      )}

      {/* Vertical layout: header → hero image + commentary overlay → replay CTA */}
      <div
        className="absolute inset-0 flex flex-col"
        style={{ padding: "16px 20px 16px", zIndex: 5 }}
      >
        {/* Header row: title left, dots right */}
        <div
          className="mb-3 flex flex-shrink-0 items-center justify-between gap-3"
          style={{ zIndex: 15 }}
        >
          <KeyPlayHeader
            playNumber={playIndex + 1}
            title={play.title}
            half={play.half}
            turn={play.turn}
            opacity={beatState.headerOpacity}
            translateX={beatState.headerTranslateX}
            rotate={beatState.headerRotate}
            scale={beatState.headerScale}
            badgeScale={beatState.badgeScale}
            badgeRotate={beatState.badgeRotate}
          />
        </div>

        {/* Hero image + commentary overlay container */}
        <div
          className="relative min-h-0 flex-1"
          style={{
            opacity: beatState.heroOpacity,
            transform: `translateY(${beatState.heroTranslateY * 0.3}px) scale(${beatState.heroScale})`,
            willChange: "opacity, transform",
          }}
        >
          {/* Hero image */}
          {play.image_url ? (
            <Button
              type="button"
              variant="ghost"
              className="h-full w-full cursor-pointer rounded-lg p-0"
              onClick={() => handleExpand("image")}
            >
              <img
                src={play.image_url}
                alt={play.title}
                className="h-full w-full rounded-lg object-cover"
              />
            </Button>
          ) : (
            <div
              className="flex h-full w-full flex-col items-center justify-center rounded-lg"
              style={{
                background:
                  "radial-gradient(ellipse at center, oklch(0.18 0.04 55), oklch(0.10 0.02 55))",
              }}
            >
              <svg
                className="mb-1 size-12"
                style={{ color: "oklch(0.30 0.06 55)" }}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="2" width="20" height="20" rx="2" />
                <circle cx="12" cy="12" r="4" />
                <path d="M2 12h4M18 12h4M12 2v4M12 18v4" />
              </svg>
              <span
                className="font-display text-center text-xs font-bold tracking-wider uppercase"
                style={{ color: "oklch(0.30 0.05 55)" }}
              >
                {play.title}
              </span>
            </div>
          )}

          {/* Dark glass overlay — dims hero as commentary enters */}
          {beatState.commentaryScrollProgress > 0 && (
            <div
              className="pointer-events-none absolute inset-0 rounded-lg"
              style={{
                background: `oklch(0.10 0.01 55 / ${0.7 * beatState.commentaryScrollProgress})`,
                backdropFilter: `blur(${12 * beatState.commentaryScrollProgress}px)`,
                WebkitBackdropFilter: `blur(${12 * beatState.commentaryScrollProgress}px)`,
              }}
            />
          )}

          {/* Floating replay button */}
          {hasReplay && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleExpand("replay")}
              className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold tracking-wider uppercase transition-colors hover:brightness-125"
              style={{
                background: "oklch(0.12 0.03 55 / 0.8)",
                backdropFilter: "blur(8px)",
                color: "var(--accent)",
                boxShadow:
                  "0 0 10px oklch(0.78 0.18 55 / 0.15), 0 2px 8px oklch(0 0 0 / 0.4)",
              }}
            >
              <Play className="size-3" fill="currentColor" />
              Replay
            </Button>
          )}

          {/* Scanline overlay for broadcast aesthetic */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                "repeating-linear-gradient(0deg, transparent, transparent 2px, oklch(0 0 0 / 0.06) 2px, oklch(0 0 0 / 0.06) 4px)",
            }}
          />
        </div>

        {/* Commentary — lines fly in from below and stack, assembling the conversation */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{ overflow: "visible", zIndex: 10 }}
        >
          <div className="px-6 pt-48">
            <div className="pointer-events-auto mx-auto max-w-4xl">
              <CommentaryDesk
                commentary={play.commentary}
                commentatorMap={commentatorMap}
                portraitUrls={recap.portrait_urls}
                opacities={beatState.commentaryOpacities}
                translateXs={beatState.commentaryTranslateX}
                translateYs={(() => {
                  const N = play.commentary.length
                  const segDur = 2 / (1 + N)
                  const step = segDur * 0.5
                  return play.commentary.map((_, idx) => {
                    const lp = Math.max(
                      0,
                      Math.min(
                        1,
                        (beatState.commentaryScrollProgress - idx * step) /
                          segDur,
                      ),
                    )
                    return viewportHeight * (1 - easeOutCubic(lp))
                  })
                })()}
                rotates={beatState.commentaryRotates}
                scales={beatState.commentaryScales}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
